import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client/react'
import { When } from 'react-if'
import Stripe from 'stripe'
import { LinkButton } from '@/atoms/Button'
import { GuildCopperIcon } from '@/atoms/Icons/GuildCopperIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsP, CaptionSM, LabelSM, ParagraphMD, TitleSM, TitleXS } from '@/atoms/Text'
import {
  ANGEL_GUILD_BASIC_MONTHLY_OFFER_ID,
  ANGEL_GUILD_BASIC_YEARLY_OFFER_ID,
  ANGEL_GUILD_PREMIUM_MONTHLY_OFFER_ID,
  ANGEL_GUILD_PREMIUM_YEARLY_OFFER_ID,
} from '@/constants/offer-ids'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { PricePerInterval } from '@/molecules/GuildPricingLabel/GuildPricingLabel'
import { GuildSignupButton } from '@/molecules/GuildSignupButton'
import { GET_THEATRICAL_RELEASES_FOR_FEATURE } from '@/services/Theatrical/queries'
import {
  GetTheatricalReleasesForFeatureQuery,
  GetTheatricalReleasesForFeatureQueryVariables,
} from '@/types/codegen-federation'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'

interface GuildIntervalViewProps {
  tier: 'basic' | 'premium'
  isSignupLayout?: boolean
}

export const GuildIntervalView: React.FC<GuildIntervalViewProps> = ({ tier, isSignupLayout }) => {
  const { t } = useTranslate('join-the-guild')
  const { sale } = useGuildSignupContext()
  const { locale } = useLocale()
  const stepNumValue = 5
  const totalValue = 5
  const isBasic = tier === 'basic'

  const { data } = useQuery<GetTheatricalReleasesForFeatureQuery, GetTheatricalReleasesForFeatureQueryVariables>(
    GET_THEATRICAL_RELEASES_FOR_FEATURE,
    { variables: { input: { language: locale } }, errorPolicy: 'all' },
  )
  const releaseToPromote = useMemo(() => {
    return data?.theatricalReleases?.theatricalRelease?.find(
      (theatricalRelease) => theatricalRelease?.isFeaturedRelease,
    )
  }, [data?.theatricalReleases?.theatricalRelease])
  const releaseTitle = releaseToPromote?.title
  const title = isBasic
    ? t('basicSubscriptionOptions', 'Basic Subscription Options')
    : t('premiumSubscriptionOptions', 'Premium Subscription Options')

  const monthlyOfferId = isBasic ? ANGEL_GUILD_BASIC_MONTHLY_OFFER_ID : ANGEL_GUILD_PREMIUM_MONTHLY_OFFER_ID
  const annualOfferId = isBasic ? ANGEL_GUILD_BASIC_YEARLY_OFFER_ID : ANGEL_GUILD_PREMIUM_YEARLY_OFFER_ID
  const monthlyPrice = isBasic ? 1200 : 2000
  const annualPrice = isBasic ? 11900 : 17900
  const annualPromotion = sale?.promotions.find((p) => p.offer.id === annualOfferId)
  const monthlyPromotion = sale?.promotions.find((p) => p.offer.id === monthlyOfferId)

  function isPromotionOneTime(promotion: Stripe.PromotionCode) {
    if (!promotion) return false
    return (
      promotion.coupon.duration === 'once' ||
      (promotion.coupon.duration === 'repeating' && promotion.coupon.duration_in_months === 1)
    )
  }

  const promotionMonths =
    monthlyPromotion?.promotionCode && isPromotionOneTime(monthlyPromotion.promotionCode)
      ? 1
      : monthlyPromotion?.promotionCode?.coupon?.duration_in_months

  return (
    <main className="relative flex min-h-screen items-start justify-center bg-core-gray-100 text-core-gray-950 lg:items-center">
      <div className="absolute left-0 top-0 hidden py-3 sm:mx-6 sm:py-4 md:flex md:justify-start">
        <InternalLink aria-label="Angel" href={paths.home}>
          <AngelLogo height={40} color="black" />
        </InternalLink>
      </div>
      <PaddedContainer className="flex min-h-[546px] max-w-[958px] flex-col justify-between !p-4 md:!px-8 md:!py-20 lg:!p-8">
        <div className="flex flex-col">
          <When condition={isSignupLayout}>
            <CaptionSM className="mb-4 font-thin tracking-widest">
              <Translate t={t} i18nKey="stepNofN" values={{ stepNumValue: stepNumValue, totalValue: totalValue }}>
                STEP {{ stepNumValue }} OF {{ totalValue }}
              </Translate>
            </CaptionSM>
          </When>
          <div className="lg:mb-22 mb-6 flex flex-row items-center gap-6">
            <Image
              src={
                'https://images.angelstudios.com/image/upload/v1700075329/angel-studios/guild/Version_Guild_Angel_Copper_OnLight_Yes.png'
              }
              alt={t('angelGuildLogo', 'Angel Guild Logo')}
              width={64}
              height={80}
              sizes="64px"
            />
            <TitleSM className="md:photon-heading-sm lg:photon-heading-lg" weight="bold">
              {t(
                'supportFilmsFilmmakersShareValues',
                'Support films & filmmakers that share your values. Cancel anytime.',
              )}
            </TitleSM>
          </div>
        </div>
        <div className="flex w-full flex-col rounded-xl bg-white p-4 lg:flex-row lg:justify-between lg:gap-16 lg:p-8">
          <div className="flex flex-col lg:max-w-[542px]">
            <div className="relative flex w-full items-center justify-between gap-2 overflow-hidden rounded-xl px-6 py-2">
              <ResponsiveBackgroundImage
                src={
                  isBasic
                    ? '/v1714422036/angel-studios/guild/card_banner-free'
                    : '/v1714422036/angel-studios/guild/card_banner-paid'
                }
                responsiveAR={{
                  xs: 4.2875,
                  sm: 8,
                  md: 8,
                  lg: 4,
                  xl: 4,
                  '2xl': 4,
                }}
                transforms="w_372,h_55,c_fill"
                gravity="g_east"
              />
              <h3 className="relative">
                <TitleXS as={AsP} weight="bold" className="md:photon-title-md lg:photon-title-lg !leading-none">
                  {title}
                </TitleXS>
                <ParagraphMD>
                  <When condition={isBasic}>
                    <Translate t={t} i18nKey="streamWithLimitedAds">
                      Stream with limited ads
                    </Translate>
                  </When>
                  <When condition={!isBasic}>
                    <Translate t={t} i18nKey="streamAdFree">
                      Stream Ad-Free
                    </Translate>
                  </When>
                </ParagraphMD>
              </h3>
              <GuildCopperIcon size={39} className="relative" shadow />
            </div>
            <div className="my-4 flex flex-col gap-4 md:flex-row">
              <div className="rounded-xl border-2 border-solid border-core-gray-300 bg-core-gray-100 p-4 md:w-1/2 lg:min-h-[356px] lg:p-6">
                <div className="flex flex-row justify-between lg:flex-col">
                  <div>
                    <TitleXS weight="semibold" className="md:photon-title-md lg:photon-heading-sm">
                      {t('monthly', 'Monthly')}
                    </TitleXS>
                    <ParagraphMD className="lg:photon-paragraph-lg">{t('billedMonthly', 'Billed monthly')}</ParagraphMD>
                  </div>
                  <div className="flex flex-col items-end lg:mt-6 lg:items-start">
                    <PricePerInterval
                      className="lg:photon-heading-xl lg:flex-col"
                      offerId={monthlyOfferId}
                      planInterval={'monthly'}
                      price={monthlyPrice}
                      monthlyPrice={monthlyPrice}
                      tier={tier}
                      disclosePriceInUSD={false}
                      separateTierPlan
                    />
                    {monthlyPromotion && promotionMonths && promotionMonths > 0 ? (
                      <CaptionSM>
                        <Translate t={t} i18nKey="forFirstSetMonths" values={{ promotionMonths }}>
                          For the first {{ promotionMonths }} months
                        </Translate>
                      </CaptionSM>
                    ) : (
                      <div className="h-4 w-full" />
                    )}
                  </div>
                </div>
                <GuildSignupButton
                  currency={'USD'}
                  offerId={monthlyOfferId}
                  price={monthlyPrice}
                  promoCode={monthlyPromotion ? monthlyPromotion?.promotionCode.code : null}
                  planInterval={'monthly'}
                  className="mt-4 w-full border-0 !bg-core-gray-300 lg:mt-12 lg:w-fit lg:min-w-[203px]"
                  buttonVariant="core-gray-400"
                >
                  <LabelSM>{t('selectMonthly', 'Select Monthly')}</LabelSM>
                </GuildSignupButton>
              </div>
              <div
                className="h-full w-full rounded-xl p-[2px] md:w-1/2"
                style={{
                  background:
                    'linear-gradient(159deg, #EDD7C6 0%, #D6A177 13.88%, #FEDEC6 40.62%, #D48D59 62.21%, #A66141 88.77%, #612B12 98.15%)',
                }}
              >
                <div className="h-full w-full rounded-[10px] bg-core-gray-100 p-4 lg:p-6">
                  <div className="flex flex-row justify-between lg:flex-col">
                    <div>
                      <TitleXS weight="semibold" className="md:photon-title-md lg:photon-heading-sm">
                        {t('annual', 'Annual')}
                      </TitleXS>
                      <ParagraphMD className="lg:photon-paragraph-lg">
                        {t('billedAnnually', 'Billed annually')}
                      </ParagraphMD>
                    </div>
                    <div className="flex flex-col items-end lg:mt-6 lg:items-start">
                      <PricePerInterval
                        className="lg:photon-heading-xl lg:min-h-[74px] lg:flex-col"
                        offerId={annualOfferId}
                        planInterval={'annual'}
                        price={annualPrice}
                        monthlyPrice={monthlyPrice}
                        tier={tier}
                        disclosePriceInUSD={false}
                        separateTierPlan
                      />
                      <CaptionSM>
                        {isBasic
                          ? t('thatsLikeTenMonth', `That's like $10/mo`)
                          : t('thatsLikeThirteenMonth', `That's like $13/mo`)}
                      </CaptionSM>
                    </div>
                  </div>
                  <GuildSignupButton
                    currency={'USD'}
                    offerId={annualOfferId}
                    price={annualPrice}
                    promoCode={annualPromotion ? annualPromotion?.promotionCode.code : null}
                    planInterval={'annual'}
                    className="mt-4 w-full lg:mt-12 lg:w-fit lg:min-w-[203px]"
                    buttonVariant="core-gray-900"
                  >
                    <LabelSM>{t('selectAnnual', 'Select Annual')}</LabelSM>
                  </GuildSignupButton>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:min-w-[218px]">
            <div className="relative my-4 md:hidden">
              <hr className="border-core-gray-300" />
            </div>
            <div className="flex flex-col lg:max-w-[218px]">
              {releaseToPromote?.images?.angelKeyArt1?.image2x3 && (
                <Image
                  src={getCloudinaryImageUrl({
                    path:
                      releaseToPromote?.theatricalSlug === slugs.sketch
                        ? 'https://images.angelstudios.com/image/upload/v1749763768/angel-studios/projects/sketch/Frame_1261158371.png'
                        : releaseToPromote.images.angelKeyArt1.image2x3,
                    width: 218,
                    height: 328,
                    transforms: 'c_fill',
                  })}
                  alt={`${releaseToPromote?.title} poster`}
                  width={218}
                  height={328}
                  sizes="(min-width: 1024px) 218px, 0px"
                  className="mx-auto my-2 hidden rounded-xl lg:flex"
                />
              )}
              {releaseToPromote?.images?.angelKeyArt1?.image16x9 && (
                <Image
                  src={getCloudinaryImageUrl({
                    path:
                      releaseToPromote?.theatricalSlug === slugs.sketch
                        ? 'https://images.angelstudios.com/image/upload/v1749763768/angel-studios/projects/sketch/Frame_1261158371.png'
                        : releaseToPromote.images.angelKeyArt1.image16x9,
                    width: 327,
                    height: 174,
                    transforms: 'c_fill',
                  })}
                  alt={`${releaseToPromote?.title} poster`}
                  width={327}
                  height={174}
                  sizes="(max-width: 1023px) min(327px, 90vw), 0px"
                  className="mb-4 w-full rounded-xl md:mx-auto md:w-3/4 lg:hidden"
                />
              )}
              <ParagraphMD weight="medium" className="text-center lg:leading-[28px]">
                <When condition={isBasic}>
                  <Translate t={t} i18nKey="basicPlanGoPremium" values={{ releaseTitle }}>
                    Go premium and get 2 tickets to {{ releaseTitle }}, plus ad-free viewing!
                  </Translate>
                </When>
                <When condition={!isBasic}>
                  <Translate t={t} i18nKey="premiumPlanIncludes" values={{ releaseTitle }}>
                    As a premium member, you get 2 free tickets to {{ releaseTitle }}, plus ad-free viewing!
                  </Translate>
                </When>
              </ParagraphMD>
              <When condition={isBasic}>
                <LinkButton
                  href={isSignupLayout ? paths.guild.signup.interval.premium : paths.guild.interval.premium}
                  variant="core-gray-400"
                  className="mt-4 w-full border-0 !bg-core-gray-300 px-5 py-[11px]"
                >
                  <LabelSM>{t('switchToPremium', 'Switch to Premium')}</LabelSM>
                </LinkButton>
              </When>
            </div>
          </div>
        </div>
      </PaddedContainer>
    </main>
  )
}
